import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './WelcomeGuidePage.css';

function WelcomeGuidePage() {
  const [openAccordion, setOpenAccordion] = useState(null);

  const toggleAccordion = (index) => {
    setOpenAccordion(openAccordion === index ? null : index);
  };

  const diningAreas = [
    {
      title: 'Opéra Garnier / Madeleine — Elegant brasseries & French dining',
      restaurants: [
        {
          name: 'Café de la Paix',
          description: 'An iconic Parisian institution offering refined French cuisine in a historic setting.',
          address: '5 Place de l\'Opéra, 75009 Paris'
        },
        {
          name: 'Bistro Volnay',
          description: 'Modern French bistronomy with excellent wines, ideal for business dinners.',
          address: '8 Rue Volney, 75002 Paris'
        },
        {
          name: 'Le Soufflé',
          description: 'A Parisian classic specializing in savory and sweet soufflés.',
          address: '36 Rue du Mont-Thabor, 75001 Paris'
        },
        {
          name: 'Chez Monsieur',
          description: 'Traditional brasserie with an elegant, warm atmosphere.',
          address: '11 Rue du Chevalier de Saint-George, 75008 Paris'
        }
      ]
    },
    {
      title: 'Sainte-Anne Street — Japanese dining & Asian options',
      restaurants: [
        {
          name: 'Kunitoraya',
          description: 'Authentic udon in a cozy setting. Perfect for a casual lunch.',
          address: '1 Rue Villedo, 75001 Paris'
        },
        {
          name: 'Higuma',
          description: 'A local favorite for ramen and donburi. Simple, fast, authentic.',
          address: '32 Rue Sainte-Anne, 75001 Paris'
        },
        {
          name: 'Aki Restaurant',
          description: 'Famous for okonomiyaki and Japanese comfort food.',
          address: '11 Rue Sainte-Anne, 75001 Paris'
        },
        {
          name: 'Juji-Ya',
          description: 'Japanese bentos, affordable and ideal for quick lunches.',
          address: '46 Rue Sainte-Anne, 75002 Paris'
        }
      ]
    },
    {
      title: 'Le Marais — trendy and diverse food options (15 min by metro)',
      restaurants: [
        {
          name: 'BigLove (Big Mamma Group)',
          description: 'Trendy Italian restaurant with wood-fired pizza and a warm, cozy vibe.',
          address: '30 Rue Debelleyme, 75003 Paris'
        },
        {
          name: 'Miznon',
          description: 'Israeli street food with creative pita dishes; energetic and delicious.',
          address: '22 Rue des Écouffes, 75004 Paris'
        },
        {
          name: 'Breizh Café',
          description: 'High-quality Breton crêpes with cider and seafood options.',
          address: '109 Rue Vieille-du-Temple, 75003 Paris'
        },
        {
          name: 'Les Philosophes',
          description: 'A classic Parisian bistro with great outdoor seating in the heart of the Marais.',
          address: '28 Rue Vieille-du-Temple, 75004 Paris'
        }
      ]
    }
  ];

  const walkingAttractions = [
    {
      name: 'Place Vendôme',
      description: 'A historic Parisian square known for its luxury boutiques and elegant architecture, just steps from the venue.',
      image: '/images/welcome/place-vendome.webp'
    },
    {
      name: 'Opéra Garnier',
      description: 'A masterpiece of 19th-century architecture, offering guided tours and one of the most beautiful opera houses in the world.',
      image: '/images/welcome/opera-garnier.webp'
    },
    {
      name: 'Jardin des Tuileries',
      description: 'A peaceful garden between the Louvre Museum and Place de la Concorde, ideal for a walk or a relaxing break.',
      image: '/images/welcome/tuileries.webp'
    },
    {
      name: 'Place de la Concorde',
      description: 'One of Paris\'s most iconic squares, featuring the Luxor Obelisk and stunning views toward the Champs-Élysées.',
      image: '/images/welcome/place-concorde.webp'
    },
    {
      name: 'Musée de l\'Orangerie',
      description: 'Famous for Monet\'s Water Lilies and an exceptional collection of Impressionist and Post-Impressionist works.',
      image: '/images/welcome/orangerie.jpg'
    }
  ];

  const metroAttractions = [
    {
      name: 'Louvre Museum',
      description: 'The world\'s largest art museum, home to masterpieces including the Mona Lisa and the Venus de Milo.',
      image: '/images/welcome/louvre.webp'
    },
    {
      name: 'Musée d\'Orsay',
      description: 'Housed in a former train station, this museum offers one of the finest Impressionist collections in the world.',
      image: '/images/welcome/orsay.webp'
    },
    {
      name: 'Eiffel Tower',
      description: 'Paris\'s most iconic monument, offering panoramic views from its three levels and the surrounding Champ de Mars.',
      image: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34'
    },
    {
      name: 'Montmartre & Sacré-Cœur',
      description: 'A historic hilltop district known for its artistic heritage, charming streets and the stunning basilica overlooking Paris.',
      image: '/images/welcome/montmartre.webp'
    },
    {
      name: 'Notre-Dame Cathedral',
      description: 'A Gothic masterpiece located on the Île de la Cité, freshly renovated, offering remarkable views of the medieval heart of Paris.',
      image: '/images/welcome/notre-dame.webp'
    }
  ];

  return (
    <div className="welcome-guide-page">
      {/* Hero Header */}
      <section className="welcome-hero">
        <div className="container">
          <h1>WELCOME GUIDE - PARIS 2026</h1>
          <p>Welcome to Paris!</p>
        </div>
      </section>

      {/* Main Content */}
      <section className="section welcome-content">
        <div className="container">
          {/* Welcome Message */}
          <div className="guide-intro">
            <p className="welcome-message">
              Paris, the City of Light, welcomes all CISAC delegates for a historic centennial edition of the
              General Assembly.
            </p>
            <p>
              Whether it is your first time in France or a familiar destination, this guide will help you
              navigate the city with ease and enjoy your stay.
            </p>
          </div>

          {/* About the City */}
          <div className="guide-card">
            <h2>About the City</h2>
            <div className="city-info-grid">
              <div className="city-info-item">
                <strong>Language:</strong> French
              </div>
              <div className="city-info-item">
                <strong>Currency:</strong> Euro (€)
              </div>
              <div className="city-info-item">
                <strong>Time zone:</strong> CET / UTC+1
              </div>
              <div className="city-info-item">
                <strong>Electricity:</strong> 230V, Type E plugs
              </div>
            </div>
          </div>

          {/* Getting to the Venue */}
          <div className="guide-card venue-section">
            <h2>Getting to the venue</h2>
            <div className="venue-image">
              <img
                src="https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=1200&h=600&fit=crop"
                alt="Pavillon Cambon Capucines"
              />
            </div>
            <p className="venue-intro">
              The event venue, <strong>Pavillon Cambon Capucines</strong>, is centrally located in the 1st
              arrondissement, just a few minutes' walk from Place Vendôme and Opéra Garnier.
            </p>
            <p className="venue-address">
              <strong>Address:</strong> 46 rue Cambon, 75001 Paris
            </p>

            <div className="airport-info">
              <h3>From Charles de Gaulle Airport (CDG)</h3>
              <p>
                <strong>Distance:</strong> ~27 km<br />
                <strong>Average travel time:</strong> 45–60 minutes
              </p>
              <p><strong>Best transportation options:</strong></p>
              <ul>
                <li>Taxi: €55–€65 (flat rate depending on zone)</li>
                <li>Ride-hailing: Uber/Bolt</li>
                <li>Train (RER B) → Metro Line 14: ~60 minutes
                  <ul>
                    <li>RER B, stop Châtelet–Les Halles</li>
                    <li>Metro line 14 to Saint-Denis Pleyel, Stop Madeleine (5mn walk from the venue)</li>
                  </ul>
                </li>
              </ul>
            </div>

            <div className="airport-info">
              <h3>From Orly Airport (ORY)</h3>
              <p>
                <strong>Distance:</strong> ~20 km<br />
                <strong>Average travel time:</strong> 35–45 minutes
              </p>
              <p><strong>Best transportation options:</strong></p>
              <ul>
                <li>Taxi: €35–€45</li>
                <li>Ride-hailing: Uber/Bolt</li>
                <li>Metro Line 14: ~45 minutes
                  <ul>
                    <li>Metro line 14 to Saint-Denis Pleyel, Stop Madeleine (5mn walk from the venue)</li>
                  </ul>
                </li>
              </ul>
            </div>
          </div>

          {/* Getting Around Paris */}
          <div className="guide-card">
            <h2>Getting around Paris</h2>
            <div className="metro-info">
              <h3>Closest metro stations to the venue:</h3>
              <ul>
                <li>Opéra (Lines 3, 7, 8) — 5 min walk</li>
                <li>Madeleine (Lines 8, 12, 14) — 5 min walk</li>
                <li>Havre–Caumartin (Lines 3, 9) — 8 min walk</li>
              </ul>
            </div>
            <div className="transport-options">
              <h3>Transport options:</h3>
              <ul>
                <li><strong>Metro & RER:</strong> Fastest option for moving across Paris</li>
                <li><strong>Taxis & Ride-hailing:</strong> Widely available</li>
                <li><strong>Walking:</strong> The area around Rue Cambon is central and easily walkable</li>
              </ul>
            </div>
          </div>

          {/* Accommodation */}
          <div className="guide-card accommodation-link-section">
            <h2>Accommodation</h2>
            <p>
              Several hotel options close to the Pavillon Cambon Capucines have been selected for delegates.
            </p>
            <p>
              Please refer to the <Link to="/accommodation" className="inline-link">Accommodation page</Link> for details and booking links.
            </p>
          </div>

          {/* Dining Near the Venue */}
          <div className="guide-card">
            <h2>Dining Near the Venue</h2>
            <p className="section-intro">The venue is located in one of the best areas of Paris for dining.</p>

            <div className="accordion-container">
              {diningAreas.map((area, index) => (
                <div key={index} className="accordion-item">
                  <button
                    className={`accordion-header ${openAccordion === `dining-${index}` ? 'active' : ''}`}
                    onClick={() => toggleAccordion(`dining-${index}`)}
                  >
                    <span>{area.title}</span>
                    <span className="accordion-icon">
                      {openAccordion === `dining-${index}` ? '−' : '+'}
                    </span>
                  </button>
                  <div className={`accordion-content ${openAccordion === `dining-${index}` ? 'open' : ''}`}>
                    {area.restaurants.map((restaurant, i) => (
                      <div key={i} className="restaurant-item">
                        <h4>{restaurant.name}</h4>
                        <p className="restaurant-description">{restaurant.description}</p>
                        <p className="restaurant-address">Address: {restaurant.address}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="dining-hours">
              <h3>French dining hours:</h3>
              <ul>
                <li>Lunch: 12:00–14:30</li>
                <li>Dinner: 19:00–22:30</li>
              </ul>
            </div>
          </div>

          {/* Things to Do in Paris */}
          <div className="guide-card">
            <h2>Things to do in Paris</h2>

            <h3 className="subsection-title">Walking distance from the venue:</h3>
            <div className="accordion-container">
              {walkingAttractions.map((attraction, index) => (
                <div key={index} className="accordion-item">
                  <button
                    className={`accordion-header ${openAccordion === `walking-${index}` ? 'active' : ''}`}
                    onClick={() => toggleAccordion(`walking-${index}`)}
                  >
                    <span>{attraction.name}</span>
                    <span className="accordion-icon">
                      {openAccordion === `walking-${index}` ? '−' : '+'}
                    </span>
                  </button>
                  <div className={`accordion-content ${openAccordion === `walking-${index}` ? 'open' : ''}`}>
                    <div className="attraction-content">
                      <img src={attraction.image.startsWith('/') ? attraction.image : `${attraction.image}?w=1200&h=450&fit=crop`} alt={attraction.name} />
                      <p>{attraction.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <h3 className="subsection-title">Short metro rides:</h3>
            <div className="accordion-container">
              {metroAttractions.map((attraction, index) => (
                <div key={index} className="accordion-item">
                  <button
                    className={`accordion-header ${openAccordion === `metro-${index}` ? 'active' : ''}`}
                    onClick={() => toggleAccordion(`metro-${index}`)}
                  >
                    <span>{attraction.name}</span>
                    <span className="accordion-icon">
                      {openAccordion === `metro-${index}` ? '−' : '+'}
                    </span>
                  </button>
                  <div className={`accordion-content ${openAccordion === `metro-${index}` ? 'open' : ''}`}>
                    <div className="attraction-content">
                      <img src={attraction.image.startsWith('/') ? attraction.image : `${attraction.image}?w=1200&h=450&fit=crop`} alt={attraction.name} />
                      <p>{attraction.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Practical Tips */}
          <div className="guide-card practical-tips">
            <h2>Practical Tips</h2>
            <div className="tips-grid">
              <div className="tip-card">
                <h4>Weather in June</h4>
                <ul>
                  <li>Warm and pleasant</li>
                  <li>Average temperatures: 18–26°C</li>
                  <li>Occasional light rain</li>
                </ul>
              </div>
              <div className="tip-card">
                <h4>Safety</h4>
                <p>Paris is generally safe, especially in central districts. Remain attentive to your belongings in crowded areas.</p>
              </div>
              <div className="tip-card">
                <h4>Visas</h4>
                <p>Please check entry requirements based on your nationality. Invitation letters available upon request.</p>
              </div>
              <div className="tip-card">
                <h4>Emergency numbers</h4>
                <ul>
                  <li>112 — General emergency</li>
                  <li>15 — Medical</li>
                  <li>17 — Police</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Closing Message */}
          <div className="closing-message">
            <h3>We look forward to welcoming you to Paris!</h3>
            <p>A unique gathering to celebrate 100 years of CISAC in one of the world's most inspiring cultural capitals.</p>
          </div>
        </div>
      </section>
    </div>
  );
}

export default WelcomeGuidePage;
